<div class="container py-4" style="max-width: 900px">
  <!-- Hero Banner -->
  <div class="booking-hero card border-0 mb-4">
    <div class="booking-hero__content">
      <div>
        <div class="booking-hero__eyebrow">Practitioner Roster</div>
        <h2 class="booking-hero__title mb-1">Invite Doctor to Practice</h2>
        <div class="booking-hero__meta">
          <span
            ><i class="bi bi-search me-1 text-primary"></i>Lookup verified medical doctors via
            unique invitation code</span
          >
        </div>
      </div>
    </div>
  </div>

  <!-- Doctor Capacity Warning -->
  @if (doctorCapReached) {
    <div class="alert alert-danger d-flex align-items-start mb-4" role="alert">
      <i class="bi bi-exclamation-circle-fill me-3 mt-1 fs-5"></i>
      <div class="flex-grow-1">
        <strong>Doctor Limit Reached</strong><br />
        <span class="small"
          >This practice has reached its maximum capacity of <strong>3 active doctors</strong>. You
          must deactivate an existing doctor before inviting a new one.</span
        >
      </div>
    </div>
  }
  <div class="card booking-card mb-4">
    <div class="card-header booking-card__header">
      <div>
        <div class="booking-card__eyebrow">Search Practitioner</div>
        <h4 class="sub-title mb-0">Search by Invitation Code</h4>
      </div>
    </div>

    <div class="card-body p-4">
      <!-- Prominent Error Alert Banner -->
      @if (errorMessage) {
        <div
          class="alert alert-danger d-flex align-items-start mb-4 p-3 rounded-3 shadow-sm border-danger border-start border-4"
          role="alert"
        >
          <i class="bi bi-exclamation-octagon-fill fs-4 me-3 text-danger flex-shrink-0 mt-1"></i>
          <div class="flex-grow-1">
            <div class="fw-bold text-danger">Action Error</div>
            <div class="small text-dark mt-1">{{ errorMessage }}</div>
          </div>
          <button
            type="button"
            class="btn-close ms-2"
            (click)="errorMessage = null"
            aria-label="Close error message"
          ></button>
        </div>
      }

      <form [formGroup]="searchForm" (ngSubmit)="search()">
        <div class="row g-3 align-items-end">
          <div class="col">
            <label for="invitationCode" class="form-label">Doctor Invitation Code</label>
            <input
              id="invitationCode"
              type="text"
              class="form-control"
              [class.is-invalid]="
                searchForm.get('invitationCode')?.invalid &&
                searchForm.get('invitationCode')?.touched
              "
              formControlName="invitationCode"
              placeholder="e.g. DOC-789456"
              autocomplete="off"
            />
            @if (
              searchForm.get('invitationCode')?.invalid && searchForm.get('invitationCode')?.touched
            ) {
              <div class="invalid-feedback">Invitation code is required.</div>
            }
          </div>
          <div class="col-auto d-flex gap-2">
            <button type="submit" class="btn btn-primary" [disabled]="isSearching">
              @if (isSearching) {
                <span
                  class="spinner-border spinner-border-sm me-1"
                  role="status"
                  aria-hidden="true"
                ></span>
                Searching...
              } @else {
                <i class="bi bi-search me-1"></i> Search
              }
            </button>
            @if (doctorResult || searchAttempted) {
              <button type="button" class="btn btn-outline-secondary" (click)="clearSearch()">
                <i class="bi bi-x-lg"></i>
              </button>
            }
          </div>
        </div>
      </form>
    </div>
  </div>

  <!-- Doctor Result Card -->
  @if (doctorResult) {
    <div class="card booking-card mb-4">
      <div class="card-header booking-card__header">
        <div>
          <div class="booking-card__eyebrow">Doctor Profile Found</div>
          <h4 class="sub-title mb-0">
            Dr. {{ doctorResult.firstName }} {{ doctorResult.lastName }}
          </h4>
        </div>
        <span class="status-pill status-completed">
          <i class="bi bi-check-circle-fill me-1"></i> Verified Practitioner
        </span>
      </div>

      <div class="card-body p-4">
        <div class="detail-matrix mb-4">
          <div class="detail-chip">
            <span class="detail-chip__label">Specialization</span>
            <span class="detail-chip__value text-primary">{{ doctorResult.specialization }}</span>
          </div>
          <div class="detail-chip">
            <span class="detail-chip__label">Email</span>
            <span class="detail-chip__value text-truncate">{{ doctorResult.email }}</span>
          </div>
          <div class="detail-chip">
            <span class="detail-chip__label">Experience</span>
            <span class="detail-chip__value">{{ doctorResult.experienceYears }} years</span>
          </div>
        </div>

        @if (doctorResult.bio) {
          <div class="notes-card notes-card--soft mb-4">
            <span class="notes-card__label">Practitioner Bio</span>
            <p class="notes-card__body mb-0">{{ doctorResult.bio }}</p>
          </div>
        }

        @if (doctorCapReached) {
          <div class="alert alert-warning mb-4">
            <i class="bi bi-exclamation-triangle-fill me-2"></i>
            <strong>Doctor Limit Reached</strong> - Deactivate an existing doctor to invite new
            ones.
          </div>
        }

        <div class="d-flex justify-content-end">
          <button
            class="btn px-4"
            [class.btn-primary]="!doctorCapReached"
            [class.btn-secondary]="doctorCapReached"
            (click)="invite()"
            [disabled]="isInviting || !clinicId || doctorCapReached"
            [title]="doctorCapReached ? 'Doctor limit reached' : 'Send invitation'"
          >
            @if (isInviting) {
              <span
                class="spinner-border spinner-border-sm me-2"
                role="status"
                aria-hidden="true"
              ></span>
              Sending Invitation...
            } @else if (doctorCapReached) {
              <i class="bi bi-lock-fill me-2"></i> Limit Reached
            } @else {
              <i class="bi bi-envelope-check me-2"></i> Send Practice Invitation
            }
          </button>
        </div>
      </div>
    </div>
  }

  <!-- No Results Message -->
  @if (searchAttempted && !doctorResult && !isSearching) {
    <div class="empty-state mb-4">
      <i class="bi bi-person-x fs-1 d-block mb-2 text-primary"></i>
      <h5>No Doctor Found</h5>
      <p class="text-muted small mb-0">
        No practitioner matches that invitation code. Please verify and retry.
      </p>
    </div>
  }

  <!-- Pending Invitations -->
  <div class="card booking-card">
    <div class="card-header booking-card__header">
      <div>
        <div class="booking-card__eyebrow">Outbox</div>
        <h4 class="sub-title mb-0">Pending Invitations</h4>
      </div>
      <button
        class="btn btn-sm btn-outline-secondary"
        (click)="loadPendingInvitations()"
        [disabled]="isLoadingPending"
      >
        <i class="bi bi-arrow-clockwise me-1"></i> Refresh
      </button>
    </div>

    <div class="card-body p-0">
      @if (isLoadingPending) {
        <div class="text-center py-4 text-muted">
          <span class="spinner-border spinner-border-sm me-2 text-primary" role="status"></span>
          Loading invitations...
        </div>
      } @else if (pendingInvitations.length === 0) {
        <div class="empty-state empty-state--compact">
          <i class="bi bi-inbox fs-2 d-block mb-1 text-primary"></i>
          <h6>No Pending Invitations</h6>
          <p class="text-muted small mb-0">No active doctor invitations waiting for acceptance.</p>
        </div>
      } @else {
        <div class="table-responsive">
          <table class="table table-hover align-middle mb-0">
            <thead>
              <tr>
                <th>Doctor</th>
                <th>Email</th>
                <th>Specialization</th>
                <th>Invited On</th>
              </tr>
            </thead>
            <tbody>
              @for (invite of pendingInvitations; track invite.invitationId) {
                <tr>
                  <td class="fw-bold text-dark">
                    Dr. {{ invite.firstName }} {{ invite.lastName }}
                  </td>
                  <td class="text-muted small">{{ invite.email }}</td>
                  <td>
                    <span
                      class="badge bg-primary-subtle text-primary border border-primary-subtle rounded-pill"
                    >
                      {{ invite.specialization }}
                    </span>
                  </td>
                  <td class="text-muted small">{{ invite.invitedOn | date: 'dd MMM yyyy' }}</td>
                </tr>
              }
            </tbody>
          </table>
        </div>
      }
    </div>
  </div>
</div>
